import { GameRouteService } from "../services/GameRouteService";
import { css, htmlArray } from "../helpers/webComponents";
import { AchievementNumberEvent, GameEventService } from "../services/GameEventService";
import { GameEvent } from "../enums/GameEvent";

/** CSS affecting the {@link AchievementComponent} */
const styles: string = css`
    :host {
        pointer-events: none;
        position: fixed;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 30%;
        overflow: hidden;
        z-index: 9999;
    }
    
    .container {
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        justify-content: center;
        width: 100%;
        height: 50%;
    }

    .achievement-div {
        position: absolute;
        display: flex;
        align-items: center;
        padding-left: 10px;
        padding-right: 10px;
        flex-direction: row;
        border-radius: 5px;
        font-size: 1.2rem;
        animation: slideInOut 5s ease-in-out;
        animation-fill-mode: forwards;
        border-radius: 10px 10px 0 0;
        border-image: url(/assets/img/ui/title-border.png) 0 370 0 490 fill round;
    }

    @keyframes slideInOut {
        0% {
            right: -50%;
        }
        20% {
            right: 10%;
        }
        80% {
            right: 10%;
        }
        100% {
            right: -50%;
        }
    }
`;

export class AchievementComponent extends HTMLElement {
    private readonly _gameRouteService: GameRouteService = new GameRouteService();
    /** Instance of the game event service */
    private readonly _gameEventService: GameEventService = new GameEventService();

    public connectedCallback(): void {
        this.attachShadow({ mode: "open" });

        // Listen for the Update Read Content event
        this._gameEventService.addGameEventListener<AchievementNumberEvent>(GameEvent.AchievementNumber, event => {
            this.render(event.number);
        });
    }

    private render(achievementNumber: number): void {
        if (!this.shadowRoot) return;
        if (achievementNumber === 0) return;
        const elements: HTMLElement[] = htmlArray`
            <style>${styles}</style>
            <div class="container">
                <div class="achievement-div">
                    <img src="${this.chooseAchievementImage(achievementNumber)}">
                    <div class="achievement-text">
                        <p id='achievement'>${this.chooseAchievement(achievementNumber)}</p>
                    </div>
                </div>
            </div>
        `;

        this.shadowRoot.append(...elements);

        this.closeTimeout(achievementNumber);
    }

    private closeTimeout(achievementNumber: number): void {
        setTimeout(() => {
            this.closeAchievement(achievementNumber);
        }, 5000);
    }

    private chooseAchievementImage(achievementNumber: number): string {
        switch (achievementNumber) {
            case 0:
                return "";
            case 1:
                return "/assets/img/achievements/achievement1.png";
            case 2:
                return "/assets/img/achievements/achievement2.png";
            case 3:
                return "/assets/img/achievements/achievement3.png";
            case 4:
                return "/assets/img/achievements/achievement4.png";
            case 5:
                return "/assets/img/achievements/achievement5.png";
            case 6:
                return "/assets/img/achievements/achievement6.png";
            case 7:
                return "/assets/img/achievements/achievement7.png";
            case 8:
                return "/assets/img/achievements/achievement8.png";
            case 9:
                return "/assets/img/achievements/achievement9.png";
            default:
                return "";
        }
    }

    /**
     * Choose the achievement text based on the achievement number.
     * @private
     * @param achievementNumber The number of the achievement
     * @returns The text of the achievement
     */

    private chooseAchievement(achievementNumber: number): string {
        switch (achievementNumber) {
            case 0:
                return "";
            case 1:
                return "Cannibalism - Eat a character";
            case 2:
                return "Button masher - Press any action button 15 times";
            case 3:
                return "Couch Potato Traveler - Ride the taxi 2 times";
            case 4:
                return "OCD Gamer - Open the inventory 5 times";
            case 5:
                return "DIY Mechanic - Try to do the welding game without all tools";
            case 6:
                return "Reuntied... Kinda - Examine the skeleton";
            case 7:
                return "Menace - Slap all possible characters";
            case 8:
                return "The End - ended the conversation with the guard in Azarath";
            case 9:
                return "I won. My therapist disagrees - fought with the sea beast and won";
            default:
                return "";
        }
    }

    private closeAchievement(achievementNumber: number): void {
        void this._gameRouteService.closeAchievement(achievementNumber);
        if (this.shadowRoot) {
            this.shadowRoot.innerHTML = "";
        }
    }
}
